package fyi.moko.hotpl.data.image

import kotlin.io.encoding.Base64
import kotlin.random.Random
import kotlinx.browser.localStorage

private const val KEY_PREFIX = "hotpl.image."
private const val GUEST_SUFFIX = "guest"

/**
 * Durable browser storage for place photos — the JS analog of Android/iOS writing to
 * an app-private directory.
 *
 * ── localStorage에서 IndexedDB로 옮긴 이유 ─────────────────────────
 * 예전에는 base64로 `localStorage`에 넣었다. 그 저장소는 origin당 5~10MB **문자열** 예산이고
 * base64가 33%를 더 먹어서, 1080px·q80 사진 기준 **15~30장이면 한도**였다. 장소·방문 JSON도
 * 같은 예산을 나눠 쓴다. 게다가 한도에 닿았을 때 조용했다 — quota 예외를 console.error로
 * 삼키고 파일명은 정상적으로 돌려줬으므로, 장소는 사진 없이 저장되고 화면에는 아무 말도
 * 뜨지 않았다. 사용자가 사진을 잃은 걸 알 방법이 없었다.
 *
 * IndexedDB는 바이너리를 그대로 담고 할당량이 보통 GB 단위다. 자세한 건 [JsImageDb].
 *
 * ── 옛 사진은 읽을 때 옮긴다 ─────────────────────────────
 * 이미 `localStorage`에 사진을 넣어 둔 사용자가 있다. 시작할 때 한 번에 옮기지 않고
 * [load]에서 못 찾았을 때만 옛 자리를 보고, 있으면 IndexedDB로 옮긴 뒤 옛 항목을 지운다.
 * 실제로 보는 사진만 옮기므로 시작이 느려지지 않고, 옮기다 중단돼도 다음에 이어진다.
 *
 * `ImagePicker.js.kt`가 다른 플랫폼과 같은 `MAX_IMAGE_DIMENSION`/`JPEG_QUALITY`로 줄여서
 * 넘긴다. 저장 키 접두사는 현재 [userIdProvider]로 계정별로 갈라진다
 * (`hotpl.image.<userId>.*`, 로그인 전에는 `hotpl.image.guest.*`) — 같은 브라우저 프로필에서
 * 계정 A의 사진이 계정 B로 새지 않게 한다.
 */
class JsPlaceImageStore(
    private val userIdProvider: suspend () -> String? = { null },
) : PlaceImageStore {

    private suspend fun prefix(): String =
        KEY_PREFIX + (userIdProvider()?.takeIf { it.isNotBlank() } ?: GUEST_SUFFIX) + "."

    /**
     * 이름은 밀리초 + 난수다. 난수 폭이 **Int 전체**여야 한다 — 예전에는 `(0..1000)`이라
     * 같은 밀리초에 20장을 저장하면 충돌 확률이 17%였고, 충돌하면 뒤 사진이 앞 사진을 같은
     * 키로 덮어썼다. 방문 하나에 사진 여럿을 붙이면 실제로 같은 밀리초에 몰린다.
     * Android(`AndroidPlaceImageStore`)가 처음부터 쓰던 폭과 맞췄다.
     */
    override suspend fun save(bytes: ByteArray): String {
        val fileName = "img_${js("Date.now()").unsafeCast<Double>().toLong()}_" +
            "${Random.nextInt(0, Int.MAX_VALUE)}.jpg"
        JsImageDb.put(prefix() + fileName, bytes)
        return fileName
    }

    override suspend fun saveAs(fileName: String, bytes: ByteArray) {
        JsImageDb.put(prefix() + fileName, bytes)
    }

    override suspend fun load(fileName: String): ByteArray? {
        val key = prefix() + fileName
        JsImageDb.get(key)?.let { return it }
        return migrateFromLocalStorage(key)
    }

    override suspend fun delete(fileName: String) {
        val key = prefix() + fileName
        runCatching { JsImageDb.delete(key) }
            .onFailure { console.error("Failed to delete stored image '$fileName'", it) }
        // 옛 자리에 남아 있을 수 있다 — 아직 한 번도 읽히지 않아 옮겨지지 않은 사진.
        runCatching { localStorage.removeItem(key) }
    }

    override suspend fun clear() {
        val p = prefix()
        runCatching {
            JsImageDb.keys(p).forEach { JsImageDb.delete(it) }
        }.onFailure { console.error("Failed to clear stored images", it) }
        runCatching {
            val stale = (0 until localStorage.length)
                .mapNotNull { localStorage.key(it) }
                .filter { it.startsWith(p) }
            stale.forEach { localStorage.removeItem(it) }
        }.onFailure { console.error("Failed to clear legacy stored images", it) }
    }

    /**
     * 옛 `localStorage` 자리에서 찾아 IndexedDB로 옮긴다. 못 찾으면 null.
     *
     * 옮기기에 실패해도 **바이트는 돌려준다** — 사진을 보여주는 게 먼저고, 옮기는 건 다음에
     * 다시 시도하면 된다. 반대로 옮기기 성공 전에는 옛 항목을 지우지 않는다.
     */
    private suspend fun migrateFromLocalStorage(key: String): ByteArray? {
        val encoded = runCatching { localStorage.getItem(key) }.getOrNull() ?: return null
        val bytes = runCatching { Base64.decode(encoded) }
            .onFailure { console.error("Failed to decode stored image '$key'", it) }
            .getOrNull() ?: return null

        // 옮기기가 실패해도 바이트는 돌려준다 — 사진을 보여주는 게 먼저이고, 옛 항목을 지우지
        // 않았으니 다음 [load]에서 다시 시도한다.
        runCatching {
            JsImageDb.put(key, bytes)
            localStorage.removeItem(key)
        }.onFailure { console.error("Failed to migrate image '$key' to IndexedDB", it) }

        return bytes
    }
}
